import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import { Drawer, DrawerContent, useDisclosure } from '@heroui/react'
import { usePostHog } from '@posthog/react'
import type { PbshoppingProductInfo } from '@itsrever/rever-api'
import { useCredit } from '@/hooks'
import { calculateAmountDue } from '../helpers/calculate-amount-due'
import {
  keepsPromotion,
  promotionalAmountDue
} from '../helpers/promotional-exchange'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { ProductDetailsDrawer } from '../product-details/product-details-drawer'
import { useAppSelector } from '@/redux/hooks'

interface StoreProductCardProps {
  product: PbshoppingProductInfo
  returnedItemId: string
}

const StoreProductCard = ({
  product,
  returnedItemId
}: StoreProductCardProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const posthog = usePostHog()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const isInStore = useAppSelector(
    (store) => store.settingsApi.settings.response.in_store_settings?.is_enabled
  )

  const { isOpen, onOpen, onOpenChange } = useDisclosure()

  const { remainingCredit, promotionalExchange, availablePromotionalUnits } =
    useCredit()

  const parsedProductPrice = product.price_range?.min?.amount
    ? parseFloat(product.price_range?.min?.amount)
    : null

  const keepsItsPromotion =
    availablePromotionalUnits > 0 &&
    keepsPromotion(promotionalExchange, {
      productId: product.id,
      collectionIds: product.collection_ids
    })

  const amountDue =
    keepsItsPromotion && promotionalExchange
      ? promotionalAmountDue(
          promotionalExchange,
          parsedProductPrice,
          remainingCredit
        )
      : parsedProductPrice !== null
        ? calculateAmountDue(parsedProductPrice, remainingCredit)
        : null

  // A replacement keeping the promotion is priced from it, whatever credit is left
  const showsAmountDue = keepsItsPromotion || remainingCredit > 0

  const mainImage = product.featured_image_url
  const secondaryImage = product.image_urls?.[1]

  const onOpenProductDetails = () => {
    posthog.capture('FC - View item details', {
      product_id: product.title,
      checkout_id: checkoutId
    })

    onOpen()
  }

  const hasPriceRange = product.price_range?.max?.amount
    ? product.price_range?.min?.amount !== product.price_range?.max?.amount
    : false

  const isHoff = ecommerceGroup === 'hoff'
  const isMisako = ecommerceGroup === 'misako'

  if (isHoff) {
    return (
      <>
        <div className="group relative flex h-full cursor-pointer flex-col gap-2.5 bg-white md:[content-visibility:auto] [&_img]:rounded-none">
          {isInStore ? (
            <span className="absolute right-2 top-2 z-10 rounded-md bg-neutral-900 p-0.5 px-1.5 text-xs font-normal text-white">
              {t('full_catalog.in_store_label')}
            </span>
          ) : null}
          {mainImage ? (
            <StoreProductImage
              mainImage={mainImage}
              secondaryImage={secondaryImage}
            />
          ) : (
            <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
              <MediaImageIcon width={28} height={28} />
            </div>
          )}
          <div className="flex items-start justify-between gap-2 bg-white">
            <div className="space-y-0.5">
              <h3>
                <button
                  onClick={onOpenProductDetails}
                  type="button"
                  className="line-clamp-2 text-left text-xs font-medium before:absolute before:inset-0 before:z-10 md:line-clamp-1"
                >
                  {product.title}
                </button>
              </h3>
              {product.description ? (
                <p
                  className="line-clamp-2 !text-xs font-normal text-neutral-500"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(product.description)
                  }}
                />
              ) : null}
            </div>
            <div>
              {showsAmountDue ? (
                <div className="flex flex-col items-end gap-0.5">
                  <p className="text-sm font-medium leading-none">
                    {amountDue !== null
                      ? amountDue?.toLocaleString(language, {
                          style: 'currency',
                          currency: product.price_range?.min?.currency
                        })
                      : null}
                  </p>
                  <p className="text-right text-xs font-normal text-neutral-600">
                    {parsedProductPrice?.toLocaleString(language, {
                      style: 'currency',
                      currency: product.price_range?.min?.currency
                    })}{' '}
                    {t('full_catalog.original_price')}
                  </p>
                </div>
              ) : parsedProductPrice !== null ? (
                <p className="text-sm leading-none text-neutral-950">
                  {hasPriceRange ? <span>{t('full_catalog.from')}</span> : null}
                  {parsedProductPrice?.toLocaleString(language, {
                    style: 'currency',
                    currency: product.price_range?.min?.currency
                  })}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <Drawer
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          placement="bottom"
          size="xl"
          classNames={{
            wrapper: 'z-[999999]',
            base: 'max-h-[90dvh] rounded-none h-full md:max-h-[36rem] md:rounded-t-md',
            closeButton: 'z-10'
          }}
        >
          <DrawerContent>
            {(onClose) => (
              <ProductDetailsDrawer
                productId={product.id}
                onClose={onClose}
                returnedItemId={returnedItemId}
              />
            )}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  if (isMisako) {
    return (
      <>
        <div className="group relative flex h-full cursor-pointer flex-col gap-2.5 bg-white md:[content-visibility:auto] [&_img]:rounded-none">
          {isInStore ? (
            <span className="absolute right-2 top-2 z-10 rounded-md bg-neutral-900 p-0.5 px-1.5 text-xs font-normal text-white">
              {t('full_catalog.in_store_label')}
            </span>
          ) : null}
          {mainImage ? (
            <StoreProductImage
              mainImage={mainImage}
              secondaryImage={secondaryImage}
            />
          ) : (
            <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
              <MediaImageIcon width={28} height={28} />
            </div>
          )}
          <div className="flex items-start justify-between gap-2 bg-white">
            <div className="space-y-0.5">
              <h3>
                <button
                  onClick={onOpenProductDetails}
                  type="button"
                  className="line-clamp-1 text-left text-xs font-light leading-normal before:absolute before:inset-0 before:z-10"
                >
                  {product.title}
                </button>
              </h3>
              {product.description ? (
                <p
                  className="line-clamp-2 !text-xs font-normal text-neutral-500"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(product.description)
                  }}
                />
              ) : null}
            </div>
            <div>
              {showsAmountDue ? (
                <div className="flex flex-col items-end gap-0.5">
                  <p className="text-sm font-light leading-normal">
                    {amountDue !== null
                      ? amountDue?.toLocaleString(language, {
                          style: 'currency',
                          currency: product.price_range?.min?.currency
                        })
                      : null}
                  </p>
                  <p className="text-xs font-light text-neutral-500 line-through">
                    {parsedProductPrice?.toLocaleString(language, {
                      style: 'currency',
                      currency: product.price_range?.min?.currency
                    })}{' '}
                  </p>
                </div>
              ) : parsedProductPrice !== null ? (
                <p className="text-sm text-neutral-950">
                  {hasPriceRange ? <span>{t('full_catalog.from')}</span> : null}
                  {parsedProductPrice?.toLocaleString(language, {
                    style: 'currency',
                    currency: product.price_range?.min?.currency
                  })}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <Drawer
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          placement="bottom"
          size="xl"
          classNames={{
            wrapper: 'z-[999999]',
            base: 'max-h-[90dvh] rounded-none h-full md:max-h-[36rem] md:rounded-t-md',
            closeButton: 'z-10'
          }}
        >
          <DrawerContent>
            {(onClose) => (
              <ProductDetailsDrawer
                productId={product.id}
                onClose={onClose}
                returnedItemId={returnedItemId}
              />
            )}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      <div className="group relative flex h-full cursor-pointer flex-col rounded-lg bg-white transition-shadow md:shadow-sm md:[content-visibility:auto] md:hover:shadow-md">
        {isInStore ? (
          <span className="absolute right-2 top-2 z-10 rounded-md bg-brand-100 p-0.5 px-1.5 text-xs font-medium">
            {t('full_catalog.in_store_label')}
          </span>
        ) : null}
        {mainImage ? (
          <StoreProductImage
            mainImage={mainImage}
            secondaryImage={secondaryImage}
          />
        ) : (
          <div className="flex size-full min-h-0 items-center justify-center rounded-lg bg-neutral-50 text-neutral-500 md:rounded-b-none">
            <MediaImageIcon width={28} height={28} />
          </div>
        )}
        <div className="space-y-2 rounded-b-lg bg-white px-2 py-2.5 md:space-y-1.5 md:p-3">
          <div className="space-y-0.5">
            <h3>
              <button
                onClick={onOpen}
                type="button"
                className="line-clamp-1 text-left text-sm font-medium before:absolute before:inset-0 before:z-10"
              >
                {product.title}
              </button>
            </h3>
            {product.description ? (
              <div
                className="line-clamp-2 font-normal text-neutral-500 [&_*]:[&_*]:text-xs"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(product.description)
                }}
              />
            ) : null}
          </div>
          <div>
            {showsAmountDue ? (
              <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-2">
                <div
                  style={{
                    boxShadow: '0px 0px 5.7px 0px #FFF inset'
                  }}
                  className="w-fit rounded-full bg-radial-gradient from-brand-800 to-brand-800 px-3 py-0.5 shadow shadow-brand-300"
                >
                  <p className="text-[0.8125rem] text-white drop-shadow-sm">
                    {amountDue !== null
                      ? amountDue?.toLocaleString(language, {
                          style: 'currency',
                          currency: product.price_range?.min?.currency
                        })
                      : null}
                  </p>
                </div>
                <p className="pl-0.5 text-xs font-normal text-neutral-600 md:pl-0">
                  {parsedProductPrice?.toLocaleString(language, {
                    style: 'currency',
                    currency: product.price_range?.min?.currency
                  })}{' '}
                  {t('full_catalog.original_price')}
                </p>
              </div>
            ) : parsedProductPrice !== null ? (
              <p className="text-sm text-neutral-950">
                {hasPriceRange ? <span>{t('full_catalog.from')}</span> : null}{' '}
                {parsedProductPrice?.toLocaleString(language, {
                  style: 'currency',
                  currency: product.price_range?.min?.currency
                })}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <Drawer
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
        size="xl"
        classNames={{
          wrapper: 'z-[999999]',
          base: 'max-h-[90dvh] rounded-none h-full md:max-h-[36rem] md:rounded-t-md',
          closeButton: 'z-10'
        }}
      >
        <DrawerContent>
          {(onClose) => (
            <ProductDetailsDrawer
              productId={product.id}
              onClose={onClose}
              returnedItemId={returnedItemId}
            />
          )}
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default memo(StoreProductCard)

interface StoreProductImageProps {
  mainImage: string
  secondaryImage?: string
}

const StoreProductImage = ({
  mainImage,
  secondaryImage
}: StoreProductImageProps) => {
  return (
    <figure className="grid">
      <img
        src={mainImage}
        height={290}
        width={230}
        fetchPriority="high"
        loading="eager"
        alt=""
        className="col-start-1 row-start-1 aspect-[4/5] min-h-0 w-full rounded-lg bg-white object-cover md:rounded-b-none"
      />
      {secondaryImage ? (
        <img
          src={secondaryImage}
          height={290}
          width={230}
          loading="lazy"
          decoding="async"
          alt=""
          className="col-start-1 row-start-1 aspect-[4/5] h-auto min-h-0 w-full rounded-lg bg-white object-cover opacity-0 transition-opacity delay-0 duration-200 will-change-[opacity] [transform:translateZ(0)] group-hover:opacity-100 md:rounded-b-none"
        />
      ) : null}
    </figure>
  )
}
